import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  ClockIcon,
  UserIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  PencilSquareIcon,
  TrashIcon,
  PlusCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowUturnLeftIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';

interface AuditEntry {
  audit_id: number;
  mg_nr: number;
  affected_member: string;
  action: string;
  changed_by_name: string;
  changed_by_mg_nr: number;
  change_source: 'admin' | 'self' | 'system';
  ip_address: string | null;
  change_date: string;
  old_values: string;
  new_values: string;
}

interface AuditHistoryModalProps {
  onClose: () => void;
}

const AuditHistoryModal: React.FC<AuditHistoryModalProps> = ({ onClose }) => {
  const [audits, setAudits] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [isVisible, setIsVisible] = useState(false);
  const [revertingId, setRevertingId] = useState<number | null>(null);
  const [confirmRevert, setConfirmRevert] = useState<AuditEntry | null>(null);
  const [revertError, setRevertError] = useState<string | null>(null);
  const [revertSuccess, setRevertSuccess] = useState<string | null>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    fetchAudits();
    setTimeout(() => setIsVisible(true), 10);
  }, []);

  const fetchAudits = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/members/audit/all?limit=200');
      if (response.data.success) {
        setAudits(response.data.audits);
      }
    } catch (err: any) {
      console.error('Error fetching audit history:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Historie');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const toggleExpand = (auditId: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(auditId)) {
        newSet.delete(auditId);
      } else {
        newSet.add(auditId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionIcon = (action: string) => {
    switch (action.toUpperCase()) {
      case 'UPDATE':
        return <PencilSquareIcon className="w-4 h-4 text-info-500" />;
      case 'DELETE':
        return <TrashIcon className="w-4 h-4 text-error-500" />;
      case 'CREATE':
        return <PlusCircleIcon className="w-4 h-4 text-success-500" />;
      default:
        return <ClockIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  const getActionBadge = (action: string) => {
    switch (action.toUpperCase()) {
      case 'UPDATE':
        return 'bg-info-100 dark:bg-info-900/30 text-info-700 dark:text-info-400';
      case 'DELETE':
        return 'bg-error-100 dark:bg-error-900/30 text-error-700 dark:text-error-400';
      case 'CREATE':
        return 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-400';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    }
  };

  const getSourceBadge = (source: string) => {
    switch (source) {
      case 'admin':
        return { label: 'Admin', class: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400' };
      case 'self':
        return { label: 'Selbst', class: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' };
      case 'system':
        return { label: 'System', class: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' };
      default:
        return { label: source || '-', class: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' };
    }
  };

  const parseJsonSafe = (jsonStr: string | null) => {
    if (!jsonStr) return null;
    try {
      return JSON.parse(jsonStr);
    } catch {
      return null;
    }
  };

  const getChangedFields = (oldValues: string, newValues: string) => {
    const oldObj = parseJsonSafe(oldValues) || {};
    const newObj = parseJsonSafe(newValues) || {};
    const changes: { field: string; old: any; new: any }[] = [];

    const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);
    allKeys.forEach(key => {
      const oldVal = oldObj[key];
      const newVal = newObj[key];
      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        changes.push({ field: key, old: oldVal, new: newVal });
      }
    });

    return changes;
  };

  const fieldLabels: Record<string, string> = {
    vorname: 'Vorname',
    name: 'Nachname',
    email: 'E-Mail',
    tel_nummer: 'Telefon',
    strasse: 'Straße',
    plz: 'PLZ',
    ort: 'Ort',
    geb_am: 'Geburtsdatum',
    gender: 'Geschlecht',
    eintritt: 'Eintrittsdatum',
    active: 'Status',
    datenschutzerklaerung: 'Datenschutz',
    homepage: 'Homepage',
    facebook: 'Facebook',
    presse: 'Presse',
    mitglied_nr: 'Mitgliedsnr.',
  };

  const formatValue = (value: any, field: string) => {
    if (value === null || value === undefined || value === '') return '-';
    if (field === 'active') return value === 1 || value === '1' ? 'Aktiv' : 'Inaktiv';
    if (field === 'gender') return value === '0' || value === 0 ? 'Männlich' : value === '1' || value === 1 ? 'Weiblich' : '-';
    if (['datenschutzerklaerung', 'homepage', 'facebook', 'presse'].includes(field)) {
      return value === 1 || value === '1' ? 'Ja' : 'Nein';
    }
    return String(value);
  };

  const handleRevert = async (audit: AuditEntry) => {
    setRevertingId(audit.audit_id);
    setRevertError(null);
    setRevertSuccess(null);

    try {
      const response = await apiClient.post(`/api/members/audit/${audit.audit_id}/revert`);
      if (response.data.success) {
        setRevertSuccess(`Änderung für ${audit.affected_member || `#${audit.mg_nr}`} wurde rückgängig gemacht`);
        setConfirmRevert(null);
        // Refresh audit list
        await fetchAudits();
        // Clear success message after 3 seconds
        setTimeout(() => setRevertSuccess(null), 3000);
      }
    } catch (err: any) {
      console.error('Error reverting audit:', err);
      setRevertError(err.response?.data?.message || 'Fehler beim Rückgängig machen');
    } finally {
      setRevertingId(null);
    }
  };

  const getRevertDescription = (audit: AuditEntry) => {
    switch (audit.action.toUpperCase()) {
      case 'UPDATE':
        return 'Die Änderungen werden auf die vorherigen Werte zurückgesetzt.';
      case 'DELETE':
        return 'Das Mitglied wird wiederhergestellt (reaktiviert).';
      case 'CREATE':
        return 'Das Mitglied wird gelöscht (soft delete).';
      default:
        return 'Die Änderung wird rückgängig gemacht.';
    }
  };

  const canRevert = (audit: AuditEntry) => {
    // REVERT actions cannot be reverted again
    if (audit.action.toUpperCase() === 'REVERT') return false;
    // UPDATE and DELETE can be reverted
    if (['UPDATE', 'DELETE'].includes(audit.action.toUpperCase())) return true;
    // CREATE can be reverted (soft delete)
    if (audit.action.toUpperCase() === 'CREATE') return true;
    return false;
  };

  return (
    <>
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
      onClick={handleClose}
    >
      <div
        className="w-full sm:max-w-4xl bg-white dark:bg-[#1c2432] border-t sm:border border-gray-200 dark:border-gray-700 rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col shadow-2xl animate-slide-up sm:animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="w-9 h-9 rounded-xl bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 grid place-items-center flex-shrink-0">
            <ClockIcon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">Änderungsverlauf</h3>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">Alle Mitgliederänderungen</p>
          </div>
          <button
            onClick={handleClose}
            className="w-9 h-9 grid place-items-center rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 text-gray-500 dark:text-gray-400 flex-shrink-0"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Success/Error Messages */}
        {revertSuccess && (
          <div className="mx-5 mt-3 p-3 bg-success-100 dark:bg-success-900/30 border border-success-200 dark:border-success-800 rounded-lg text-sm text-success-700 dark:text-success-400">
            {revertSuccess}
          </div>
        )}
        {revertError && (
          <div className="mx-5 mt-3 p-3 bg-error-100 dark:bg-error-900/30 border border-error-200 dark:border-error-800 rounded-lg text-sm text-error-700 dark:text-error-400">
            {revertError}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600 p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
                    <div className="flex-1 space-y-1.5">
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-32 bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
                        <div className="h-4 w-16 bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
                      </div>
                      <div className="h-3 w-48 bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
                    </div>
                    <div className="w-4 h-4 bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-12 text-error-500">{error}</div>
          ) : audits.length === 0 ? (
            <div className="text-center py-12 text-gray-500">Keine Einträge vorhanden</div>
          ) : (
            <div className="space-y-2">
              {audits.map((audit) => {
                const isExpanded = expandedRows.has(audit.audit_id);
                const changes = getChangedFields(audit.old_values, audit.new_values);
                const sourceBadge = getSourceBadge(audit.change_source);

                return (
                  <div
                    key={audit.audit_id}
                    className="bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden"
                  >
                    {/* Main Row */}
                    <div
                      className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      onClick={() => toggleExpand(audit.audit_id)}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        {/* Action Icon */}
                        <div className="flex-shrink-0">
                          {getActionIcon(audit.action)}
                        </div>

                        {/* Info */}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {audit.affected_member || `#${audit.mg_nr}`}
                            </span>
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${getActionBadge(audit.action)}`}>
                              {audit.action}
                            </span>
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${sourceBadge.class}`}>
                              {sourceBadge.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            <span>{formatDate(audit.change_date)}</span>
                            <span>von {audit.changed_by_name || 'System'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Expand Icon */}
                      <div className="flex-shrink-0 ml-2">
                        {isExpanded ? (
                          <ChevronUpIcon className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="px-3 pb-3 pt-1 border-t border-gray-200 dark:border-gray-600">
                        {/* IP Address */}
                        {audit.ip_address && (
                          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-2">
                            <ComputerDesktopIcon className="w-3.5 h-3.5" />
                            <span>IP: {audit.ip_address}</span>
                          </div>
                        )}

                        {/* Changes Table */}
                        {changes.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-gray-500 dark:text-gray-400">
                                  <th className="text-left py-1 pr-2 font-medium">Feld</th>
                                  <th className="text-left py-1 pr-2 font-medium">Alt</th>
                                  <th className="text-left py-1 font-medium">Neu</th>
                                </tr>
                              </thead>
                              <tbody>
                                {changes.map((change, idx) => (
                                  <tr key={idx} className="border-t border-gray-200 dark:border-gray-600">
                                    <td className="py-1.5 pr-2 font-medium text-gray-700 dark:text-gray-300">
                                      {fieldLabels[change.field] || change.field}
                                    </td>
                                    <td className="py-1.5 pr-2 text-error-600 dark:text-error-400">
                                      {formatValue(change.old, change.field)}
                                    </td>
                                    <td className="py-1.5 text-success-600 dark:text-success-400">
                                      {formatValue(change.new, change.field)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500">Keine Detailänderungen verfügbar</p>
                        )}

                        {/* Revert Button */}
                        {canRevert(audit) && (
                          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmRevert(audit);
                              }}
                              disabled={revertingId === audit.audit_id}
                              className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 hover:bg-amber-200 dark:hover:bg-amber-900/50 rounded-lg transition-colors disabled:opacity-50"
                            >
                              <ArrowUturnLeftIcon className="w-3.5 h-3.5" />
                              Rückgängig machen
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>

      {/* Confirmation Dialog */}
      {confirmRevert && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setConfirmRevert(null)}
          />
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
                <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Änderung rückgängig machen?
              </h3>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                {getRevertDescription(confirmRevert)}
              </p>
              <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-gray-900 dark:text-white">
                    {confirmRevert.affected_member || `#${confirmRevert.mg_nr}`}
                  </span>
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${getActionBadge(confirmRevert.action)}`}>
                    {confirmRevert.action}
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formatDate(confirmRevert.change_date)} von {confirmRevert.changed_by_name || 'System'}
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setConfirmRevert(null)}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={() => handleRevert(confirmRevert)}
                disabled={revertingId !== null}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {revertingId !== null ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Wird rückgängig gemacht...
                  </>
                ) : (
                  <>
                    <ArrowUturnLeftIcon className="w-4 h-4" />
                    Rückgängig machen
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AuditHistoryModal;
